import type {
  Notification,
  NotificationListResponse,
  NotificationListParams,
  UnreadCountResponse,
} from '../types/notifications';
import { authFetch } from '../lib/authFetch';
import { buildQueryString } from '../lib/query';
import { resolveBase, json } from './client';
import type { ApiErrorPayload, PaginationMeta } from './contracts';

const _DEFAULT_PAGE_SIZE = 50;

type NotificationListApiResponse = ApiErrorPayload & {
  items?: Notification[];
  pagination?: PaginationMeta;
};

type UnreadCountApiResponse = ApiErrorPayload & {
  count?: number;
  has_unread?: boolean;
};

type NotificationMutationApiResponse = ApiErrorPayload &
  Partial<Notification> & {
    updated_count?: number;
  };

export async function listNotifications(
  apiBase: string,
  params: NotificationListParams = {}
): Promise<NotificationListResponse> {
  const base = resolveBase(apiBase);
  const query = buildQueryString(
    {
      page: params.page ? params.page : undefined,
      page_size: params.page_size ? params.page_size : undefined,
      notification_type: params.notification_type || undefined,
      read: params.read === undefined ? undefined : params.read,
      resolved: params.resolved === undefined ? undefined : params.resolved,
      starred: params.starred === undefined ? undefined : params.starred,
      module: params.module || undefined,
      related_module: params.related_module || undefined,
    },
    { includeQuestionMark: true },
  );

  const url = `${base}/api/notifications${query}`;
  const response = await authFetch(url, {
    headers: {
      'X-Skip-Universal-Log': 'true',
    },
  });
  const data = await json<NotificationListApiResponse>(response);
  
  if (data.status === 'success') {
    return {
      status: 'success',
      items: data.items || [],
      pagination: data.pagination || {
        page: 1,
        page_size: _DEFAULT_PAGE_SIZE,
        total: 0,
        total_pages: 1,
      },
    };
  }
  return data as NotificationListResponse;
}

export async function getUnreadCount(apiBase: string): Promise<UnreadCountResponse> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/notifications/unread-count`, {
    headers: {
      'X-Skip-Universal-Log': 'true',
    },
  });
  const data = await json<UnreadCountApiResponse>(response);
  
  if (data.status === 'success') {
    return {
      status: 'success',
      count: data.count || 0,
      has_unread: data.has_unread || false,
    };
  }
  return data as UnreadCountResponse;
}

export async function markAsRead(
  apiBase: string,
  notificationId: string,
  read: boolean = true
): Promise<Notification> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/notifications/${notificationId}/read`, {
    method: 'PATCH',
    body: JSON.stringify({ read }),
  });
  const data = await json<NotificationMutationApiResponse>(response);
  
  if (data.status === 'success') {
    const { status, ...notification } = data;
    return notification as Notification;
  }
  throw new Error(data.message || 'Failed to mark notification as read');
}

export async function markAllAsRead(apiBase: string): Promise<{ updated_count: number }> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/notifications/read-all`, {
    method: 'PATCH',
  });
  const data = await json<NotificationMutationApiResponse>(response);
  
  if (data.status === 'success') {
    return {
      updated_count: data.updated_count || 0,
    };
  }
  throw new Error(data.message || 'Failed to mark all as read');
}

export async function markAsResolved(
  apiBase: string,
  notificationId: string,
  resolved: boolean = true
): Promise<Notification> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/notifications/${notificationId}/resolved`, {
    method: 'PATCH',
    body: JSON.stringify({ resolved }),
  });
  const data = await json<NotificationMutationApiResponse>(response);

  if (data.status === 'success') {
    const { status, ...notification } = data;
    return notification as Notification;
  }
  throw new Error(data.message || 'Failed to update notification status');
}

export async function markAllAsResolved(apiBase: string): Promise<{ updated_count: number }> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/notifications/resolved-all`, {
    method: 'PATCH',
  });
  const data = await json<NotificationMutationApiResponse>(response);

  if (data.status === 'success') {
    return {
      updated_count: data.updated_count || 0,
    };
  }
  throw new Error(data.message || 'Failed to resolve notifications');
}

export async function markAsStarred(
  apiBase: string,
  notificationId: string,
  starred: boolean = true
): Promise<Notification> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/notifications/${notificationId}/star`, {
    method: 'PATCH',
    body: JSON.stringify({ starred }),
  });
  const data = await json<NotificationMutationApiResponse>(response);

  if (data.status === 'success') {
    const { status, ...notification } = data;
    return notification as Notification;
  }
  throw new Error(data.message || 'Failed to star notification');
}

export async function deleteNotification(apiBase: string, notificationId: string): Promise<void> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/notifications/${notificationId}`, {
    method: 'DELETE',
  });
  const data = await json<NotificationMutationApiResponse>(response);
  
  if (data.status !== 'success') {
    throw new Error(data.message || 'Failed to delete notification');
  }
}
